import React, { Fragment, useState, useEffect } from "react";
import { Field } from "react-final-form";
import { Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import column_types from "./column_types";
import { useFormState } from "react-final-form";
import { InputField, ToggleField, required } from "modules/4_final_form/mobile";

import { f7, Tabs, Tab, Button, List, Block } from "framework7-react";
import {
  conditionalCheck,
  getColsAttributes,
  fetchRequiredFormFieldData,
  getActiveHoldPoints,
  holdBypassPath,
  isHoldBypassed,
  valueMatchesHold,
} from "./shared";
import _ from "lodash";
import { connect } from "react-redux";
import { getEmployees } from "modules/employees/actions";
import { getProjects } from "modules/projects/actions";
import { getUserManagements } from "modules/user_managements/actions";
import { getAddressBookCompanies } from "modules/address_book/companies/actions";
import { useForm } from "react-final-form";

const MobileFormViewer = (props) => {
  const { custom_form, handleSubmit, formLabel, edit, disabled, disabledMessage } = props;
  const latest_revision = _.last(custom_form?.revisions_attributes);
  const pages_attributes = latest_revision?.pages_attributes || [];

  let pages_to_display = _.filter(pages_attributes, (page) => {
    if (edit) {
      return page.show_on_update;
    } else {
      return page.show_on_create;
    }
  });

  const [activeTab, setActiveTab] = useState(0);
  const { values, errors } = useFormState();
  const form = useForm();

  const cols = getColsAttributes(custom_form);
  const activeHoldPoints = getActiveHoldPoints(cols, values);

  // ponytail: fields no longer fetch lists — one shot here so N inputs don't N-hit the API
  useEffect(() => {
    fetchRequiredFormFieldData(cols, props);
  }, [custom_form?.id]);

  // ponytail: panel also has .page-content — hit active main page; rAF beats F7 tab show nudge
  useEffect(() => {
    const scrollToTop = () => {
      const el =
        f7?.views?.main?.router?.currentPageEl?.querySelector(".page-content") ||
        document.querySelector(".view-main .page-current .page-content");
      if (el) el.scrollTop = 0;
    };
    scrollToTop();
    requestAnimationFrame(scrollToTop);
  }, [activeTab]);
  const commentsReqdOnLastPage = _.some(values?.comments, (value, name) => {
    if (name === "__hold_bypasses") return false;
    const findCol = _.find(cols, { name: name });
    return findCol?.show_comments_at_the_end;
  });

  const holdPointBlockMessages = _.map(activeHoldPoints, (col) => {
    const bypass = _.get(values, holdBypassPath(col.name));
    if (col.hold_point_bypassable && bypass?.bypassed && !_.trim(bypass?.comment || "")) {
      return `${col.label || col.name}: bypass comment is required`;
    }
    return col.hold_point_message || `Hold point: ${col.label || col.name} — save as draft only`;
  });

  const attemptProperSubmit = () => {
    if (_.isNotEmpty(holdPointBlockMessages)) {
      toastr.error(holdPointBlockMessages.join("\n"));
      return;
    }
    form.change("draft", false);
    handleSubmit();
  };

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  pages_to_display = _.filter(pages_to_display, (page, i) => {
    const previousPages = _.slice(pages_to_display, 0, i);
    const previousPagesWithComplete = _.map(previousPages, (page) => {
      const findPageData = _.find(values?.page_data_attributes, { page_guid: page?.guid });
      return { ...page, complete: findPageData?.complete };
    });

    let hidePage = _.some(previousPagesWithComplete, (page) => !page?.complete && page?.hide_next_steps);

    if (page?.conditionally_visible) {
      const last_revision = _.last(custom_form?.revisions_attributes);
      const rows = _.flatMap(last_revision?.pages_attributes, "data.rows_attributes");
      const columns = _.flatMap(rows, "cols_attributes");

      const findConditionalColumn = _.find(columns, { name: page?.conditional_field });
      const conditionalValue = _.get(values?.data, page?.conditional_field);

      if (conditionalValue) {
        hidePage = conditionalCheck(
          findConditionalColumn?.column_type,
          page?.conditional_value,
          page?.conditional_selected_value,
          page?.conditional_boolean_value,
          conditionalValue
        );
      } else {
        hidePage = true;
      }
    }

    return !hidePage;
  });

  return (
    <Fragment>
      {custom_form?.show_tabs && (
        <Nav tabs>
          {_.map(pages_to_display, (page, i) => {
            return (
              <NavItem key={i}>
                <NavLink
                  className={classnames({ active: activeTab === i })}
                  onClick={() => {
                    if (custom_form?.enable_tab_nav) {
                      toggle(i);
                    }
                  }}
                  disabled={!custom_form?.enable_tab_nav}
                >
                  {page.name}
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
      )}
      <Tabs>
        {_.map(pages_to_display, (page, i) => {
          const isFirstPage = i == 0;
          const isLastPage = i == pages_to_display.length - 1 && !commentsReqdOnLastPage;
          const thisPageCols = _.flatMap(page?.data?.rows_attributes, "cols_attributes");
          const thisPageInputNames = _.map(thisPageCols, "name");
          const thisPageHasAttachments = _.some(thisPageCols, (col) => {
            return col?.column_type == "attachments";
          });

          const inputsHasErrors = _.some(thisPageInputNames, (inputName) => {
            return _.has(errors?.data, inputName);
          });

          const attachmentsHasErrors = thisPageHasAttachments && _.has(errors, "attachments_attributes");
          const thisPageHasErrors = inputsHasErrors || attachmentsHasErrors;

          const togglePrev = () => {
            toggle(i - 1);
          };
          const toggleNext = () => {
            toggle(i + 1);
          };

          return (
            <Tab tabId={i} key={i} tabActive={i == activeTab}>
              <PageViewer custom_form={custom_form} page={page?.data} cols={cols} values={values} isLastPage={isLastPage} />
              <hr />
              {edit && (
                <Fragment>
                  <List>
                    <Field slot="list" component={ToggleField} color="red" name="archived" label="Archived" />
                  </List>
                  <hr />
                </Fragment>
              )}
              <FooterButtons
                togglePrev={togglePrev}
                toggleNext={toggleNext}
                isFirstPage={isFirstPage}
                isLastPage={isLastPage}
                handleSubmit={attemptProperSubmit}
                thisPageHasErrors={thisPageHasErrors}
                hasActiveHoldPoints={_.isNotEmpty(activeHoldPoints)}
                holdPointBlockMessages={holdPointBlockMessages}
                custom_form={custom_form}
                formLabel={formLabel}
                disabled={disabled}
                disabledMessage={disabledMessage}
                i={i}
              />
            </Tab>
          );
        })}
        {commentsReqdOnLastPage && (
          <Tab tabId={pages_to_display.length} tabActive={pages_to_display.length == activeTab}>
            <CommentsPage {...props} />
            <hr />
            <FooterButtons
              togglePrev={() => toggle(pages_to_display.length - 1)}
              toggleNext={attemptProperSubmit}
              isFirstPage={false}
              isLastPage={true}
              thisPageHasErrors={false}
              hasActiveHoldPoints={_.isNotEmpty(activeHoldPoints)}
              holdPointBlockMessages={holdPointBlockMessages}
              formLabel={formLabel}
              custom_form={custom_form}
              handleSubmit={attemptProperSubmit}
              disabled={disabled}
              disabledMessage={disabledMessage}
            />
          </Tab>
        )}
      </Tabs>
    </Fragment>
  );
};

export default connect(null, { getEmployees, getProjects, getUserManagements, getAddressBookCompanies })(MobileFormViewer);

const FooterButtons = (props) => {
  const {
    togglePrev,
    toggleNext,
    isLastPage,
    handleSubmit,
    isFirstPage,
    thisPageHasErrors,
    hasActiveHoldPoints,
    holdPointBlockMessages,
    formLabel,
    custom_form,
    disabled,
    disabledMessage,
  } = props;

  const [submitting, setSubmitting] = useState(false);

  const getSaveBlockReasons = () => {
    const reasons = [];
    if (disabled && disabledMessage) reasons.push(disabledMessage);
    if (thisPageHasErrors) reasons.push("Please fix the validation errors on this page.");
    if (_.isNotEmpty(holdPointBlockMessages)) reasons.push(...holdPointBlockMessages);
    return reasons;
  };

  const showBlockedModal = (reasons) => {
    f7.dialog.alert(reasons.join("\n\n"), "Cannot save and complete");
  };

  const handleSubmitButton = () => {
    // Non-workflow: always allow Next. Workflow keeps error/hold gating.
    if (!isLastPage && (!custom_form?.workflow_style || (!thisPageHasErrors && !hasActiveHoldPoints))) {
      toggleNext();
      return;
    }

    if (isLastPage) {
      const reasons = getSaveBlockReasons();
      if (_.isNotEmpty(reasons)) {
        showBlockedModal(reasons);
        return;
      }
      setSubmitting(true);
      handleSubmit();
      return;
    }

    if (thisPageHasErrors || hasActiveHoldPoints) {
      handleSubmit();
    }
  };

  return (
    <div className="grid grid-cols-2 grid-gap margin-left margin-right">
      {!isFirstPage ? (
        <Button color="red" fill size="sm" onClick={togglePrev}>
          Previous
        </Button>
      ) : (
        <div />
      )}
      <Button
        size="sm"
        color="green"
        fill
        disabled={submitting}
        onClick={handleSubmitButton}
        style={disabled && isLastPage ? { opacity: 0.65 } : undefined}
      >
        {isLastPage ? <>{formLabel}</> : "Next"}
      </Button>
    </div>
  );
};

const PageViewer = (props) => {
  const {
    page: { rows_attributes },
    isLastPage,
    values,
    cols,
    custom_form,
  } = props;

  return (
    <List>
      {_.map(rows_attributes, (row, i) => {
        return <RowViewer slot="list" row={row} values={values} cols={cols} custom_form={custom_form} key={i} />;
      })}
    </List>
  );
};

const RowViewer = (props) => {
  const {
    row: { cols_attributes },
    values,
    cols,
    custom_form,
  } = props;

  return (
    <Fragment>
      {_.map(cols_attributes, (column, i) => {
        return <ColumnViewer column={column} values={values} cols={cols} custom_form={custom_form} key={i} />;
      })}
    </Fragment>
  );
};

const ColumnViewer = (props) => {
  const { column, cols, values, custom_form } = props;
  const { conditionally_visible, conditional_field, conditional_value } = column;
  const ColumnType = column_types.find((c) => c.value == column.column_type);
  const SelectedFormComponent = ColumnType?.mobileFormComponent || ColumnType?.formComponent;

  let visible = true;
  if (conditionally_visible) {
    const findConditionalColumnType = _.find(cols, { name: conditional_field })?.column_type;
    const findConditionalColumnValue = _.get(values?.data, conditional_field);

    if (findConditionalColumnType == "toggle") {
      if (conditional_value == "true" || conditional_value === true) {
        visible = findConditionalColumnValue;
      } else if (conditional_value == "false" || conditional_value === false || conditional_value == undefined || conditional_value === null) {
        visible = !findConditionalColumnValue;
      }
    } else if (_.isArray(conditional_value)) {
      if (conditional_value?.[0].label == "Present") {
        visible = _.isNotEmpty(findConditionalColumnValue);
      } else {
        const conditional_values = _.map(conditional_value, (v) => v.label.toLowerCase());
        if (_.isArray(findConditionalColumnValue)) {
          const findConditionalColumnArrayValues = _.map(findConditionalColumnValue, (v) => v?.value.toLowerCase());
          visible = _.some(conditional_values, (val) => findConditionalColumnArrayValues.includes(val));
        } else {
          visible = _.includes(conditional_values, findConditionalColumnValue?.toLowerCase?.());
        }
      }
    } else {
      visible = conditional_value == findConditionalColumnValue;
    }
  }

  const holdMatching = column.hold_point && valueMatchesHold(column.column_type, column.hold_point_value, _.get(values?.data, column.name));
  const holdBypassed = holdMatching && isHoldBypassed(column, values);
  const bypassOn = !!_.get(values, `${holdBypassPath(column.name)}.bypassed`);

  if (SelectedFormComponent && visible) {
    return (
      <Fragment>
        <SelectedFormComponent custom_form={custom_form} {...column} />
        {holdMatching && (
          <Block strong inset style={{ backgroundColor: "orange"}}>
            <li>
              <div className={`item-content ${holdBypassed ? "text-color-green" : "text-color-orange"}`}>
                <div className="item-inner">
                  <div className="item-title" style={{ whiteSpace: "normal" }}>
                    <strong>{holdBypassed ? "Hold point bypassed." : "Hold point active."}</strong>{" "}
                    {!holdBypassed && " Save as draft only until cleared or bypassed."}
                    <br />
                    {column.hold_point_message || `${column.label || column.name}`}
                  </div>
                </div>
              </div>
            </li>
            {column.hold_point_bypassable && (
              <Fragment>
                <Field slot="list" name={`${holdBypassPath(column.name)}.bypassed`} component={ToggleField} label="Bypass hold point" />
                {bypassOn && (
                  <Field
                    slot="list"
                    name={`${holdBypassPath(column.name)}.comment`}
                    component={InputField}
                    type="textarea"
                    label="Bypass comment (required)"
                    placeholder="Reason for bypass"
                    parse={(v) => v}
                    validate={required}
                  />
                )}
              </Fragment>
            )}
          </Block>
        )}
      </Fragment>
    );
  }
  return null;
};

const CommentsPage = (props) => {
  const { values, errors } = useFormState();

  const { comments } = values;
  const cols = getColsAttributes(props.custom_form);

  return (
    <Fragment>
      {_.map(comments, (value, comment_name) => {
        const findCol = _.find(cols, { name: comment_name });

        if (comments && findCol?.show_comments_at_the_end) {
          return (
            <List key={comment_name}>
              <Field
                slot="list"
                type="textarea"
                component={InputField}
                label={`${findCol?.label} Comments`}
                parse={(v) => v}
                name={`comments.${comment_name}`}
              />
            </List>
          );
        }
      })}
    </Fragment>
  );
};
